import React, { useCallback, createContext, useContext } from "react";
import { atomFamily, atomWithDefault } from "jotai/utils";
import { type PrimitiveAtom, useAtom } from "jotai";
import { useQuery, useMutation } from "@tanstack/react-query";
import { type QueryFunction } from "@tanstack/query-core";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Tooltip,
} from "@mui/material";
import { Close } from "@mui/icons-material";

// Generic context interface
interface AsyncModalContextValue<
  TData = any,
  TSubmitData = any,
  TSubmitResponse = any
> {
  data: TData | null;
  isLoading: boolean;
  error: Error | null;
  submitModal: (data?: TSubmitData) => Promise<TSubmitResponse>;
  isSubmitting: boolean;
  closeModal: () => void;
}

// Create context with generic type
const AsyncModalContext = createContext<AsyncModalContextValue | null>(null);

// Generic hook with type parameters
export function useAsyncModal<
  TData = any,
  TSubmitData = any,
  TSubmitResponse = any
>(): AsyncModalContextValue<TData, TSubmitData, TSubmitResponse> {
  const context = useContext(AsyncModalContext) as AsyncModalContextValue<
    TData,
    TSubmitData,
    TSubmitResponse
  > | null;

  if (!context) {
    throw new Error("useAsyncModal must be used within AsyncModal");
  }
  return context;
}

// Generic modal state interface
interface AsyncModalState<
  TData = any,
  TSubmitData = any,
  TSubmitResponse = any
> {
  open: boolean;
  title: string;
  description?: string;
  actionTitle: string;
  cancelTitle?: string;
  submitHandler?: (data?: TSubmitData) => Promise<TSubmitResponse>;
  actionIcon?: React.ReactNode;
  closeHandler?: () => void;
  loadDataQueryFn?: QueryFunction<TData>;
  showActions?: boolean;
  disableBackdropClick?: boolean;
  maxWidth?: "xs" | "sm" | "md" | "lg" | "xl";
}

interface BaseAsyncModalState extends AsyncModalState<any, any, any> {}

// Create the atom family with atomWithDefault for a writable atom
export const asyncModalStateAtom = atomFamily<
  string[],
  PrimitiveAtom<BaseAsyncModalState>
>(
  (modalKey) =>
    atomWithDefault<BaseAsyncModalState>((get) => ({
      open: false,
      title: "",
      actionTitle: "Submit",
      cancelTitle: "Cancel",
      showActions: true,
      disableBackdropClick: false,
      maxWidth: "md",
      loadDataQueryFn: async () => {},
      submitHandler: async () => {},
    })),
  (a, b) => JSON.stringify(a) === JSON.stringify(b)
);

// Generic modal props
interface AsyncModalProps<
  TData = any,
  TSubmitData = any,
  TSubmitResponse = any
> {
  modalKey: string[];
  children:
    | React.ReactNode
    | ((
        props: AsyncModalContextValue<TData, TSubmitData, TSubmitResponse>
      ) => React.ReactNode);
}

// Generic modal component
export function AsyncModal<
  TData = any,
  TSubmitData = any,
  TSubmitResponse = any
>({
  modalKey,
  children,
}: AsyncModalProps<TData, TSubmitData, TSubmitResponse>) {
  // You'll need to create typed atoms for each modal instance
  // This is a simplified version - in practice you might want to use a registry

  const [asyncModalState, setAsyncModalState] = useAtom(
    asyncModalStateAtom(modalKey)
  );

  console.log("asyncModalState", asyncModalState, modalKey);
  const handleClose = useCallback(
    (_: any, reason: string) => {
      if (
        asyncModalState.disableBackdropClick &&
        (reason === "backdropClick" || reason === "escapeKeyDown")
      ) {
        return;
      }
      asyncModalState.closeHandler?.();
      setAsyncModalState((prev) => ({ ...prev, open: false }));
    },
    [asyncModalState, setAsyncModalState]
  );

  const loadData = useQuery<TData>({
    queryKey: ["asyncModalLoadKey", ...modalKey],
    queryFn: asyncModalState.loadDataQueryFn,
    enabled: asyncModalState.open,
  });

  const submitMutation = useMutation<TSubmitResponse, Error, TSubmitData>({
    mutationFn: asyncModalState.submitHandler,
    onSuccess: (data) => {
      handleClose({}, "submitSuccess");
    },
    onError: (error) => {
      console.error("Submit error:", error);
    },
  });

  const submitModal = useCallback(
    async (data?: TSubmitData) => {
      return await submitMutation.mutateAsync(data!);
    },
    [submitMutation]
  );

  const contextValue: AsyncModalContextValue<
    TData,
    TSubmitData,
    TSubmitResponse
  > = {
    data: loadData.data ?? null,
    isLoading: loadData.isLoading,
    error: loadData.error ?? submitMutation.error,
    submitModal,
    isSubmitting: submitMutation.isPending,
    closeModal: () => {
      handleClose({}, "manual");
    },
  };

  const renderChildren = () => {
    if (typeof children === "function") {
      return children(contextValue);
    }
    return children;
  };

  return (
    <Dialog
      open={asyncModalState.open}
      onClose={contextValue.closeModal}
      maxWidth={asyncModalState.maxWidth}
      fullWidth={true}
      scroll={"paper"}
    >
      <DialogTitle sx={{ paddingRight: "8px", paddingTop: "10px" }}>
        <Box display="flex" alignItems="center">
          <Box flexGrow={1} paddingRight="6px">
            {asyncModalState.title}
          </Box>
          <Box>
            <Tooltip title="Close">
              <span>
                <IconButton
                  disabled={submitMutation.isPending}
                  onClick={() => {
                    handleClose({}, "closeButton");
                  }}
                  size="small"
                >
                  <Close />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Box>
      </DialogTitle>

      {asyncModalState.description && (
        <DialogContent>
          <DialogContentText>{asyncModalState.description}</DialogContentText>
        </DialogContent>
      )}
      <AsyncModalContext.Provider value={contextValue}>
        {loadData.isLoading ? (
          <DialogContent>
            <Box display="flex" justifyContent="center" padding={3}>
              <CircularProgress />
            </Box>
          </DialogContent>
        ) : (
          renderChildren()
        )}

        {asyncModalState.showActions && !loadData.isLoading && (
          <DialogActions>
            <Button
              onClick={() => {
                handleClose({}, "cancel");
              }}
              disabled={submitMutation.isPending}
            >
              {asyncModalState.cancelTitle}
            </Button>
            <Button
              onClick={async () => await submitModal()}
              variant="contained"
              disabled={submitMutation.isPending}
              startIcon={
                submitMutation.isPending ? (
                  <CircularProgress size={20} />
                ) : (
                  asyncModalState.actionIcon
                )
              }
            >
              {asyncModalState.actionTitle}
            </Button>
          </DialogActions>
        )}
      </AsyncModalContext.Provider>
    </Dialog>
  );
}
